import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import fallbackImage from '../assets/stores/robu.jpg';
import './ImageMarquee.css';

const COMPONENT_SUBCATEGORIES = [
  'Development Boards',
  'Sensor Modules',
  'Semiconductors & Prototyping Components',
  'Batteries, Power Supplies & Accessories',
  'Wireless & IoT Products',
  'Switches, Relays & Protection',
  'Lighting & LEDs',
  'Motors, Drivers & Pumps',
  'Wires, Cables & Connectors',
  'Robotics & DIY Learning Kits',
  '3D Printers & Parts',
  'SMD Components',
  'Electronic Modules',
  'Drone Components',
  'Hardware Instruments & Tools',
  'Displays',
];

const CATEGORY_GROUPS = [
  { title: 'Components', items: COMPONENT_SUBCATEGORIES },
  { title: '3D Design', items: ['CAD Models', 'Parametric Parts', 'STL/STEP Exports'] },
  { title: 'Schematics', items: ['Circuit Diagrams', 'PCB Layout References', 'BOM Mapping'] },
  { title: 'Services', items: ['Custom Sourcing', 'BOM Optimization', 'Prototype Guidance'] },
];

function loadImagesFromDesktopFolder() {
  try {
    const ctx = require.context('../assets/marquee/desktop', false, /\.(png|jpe?g|webp|gif)$/i);
    const keys = ctx.keys();
    return keys.map((key) => ctx(key));
  } catch {
    return [];
  }
}

function loadImagesFromMobileFolder() {
  try {
    const ctx = require.context('../assets/marquee/mobile', false, /\.(png|jpe?g|webp|gif)$/i);
    const keys = ctx.keys();
    return keys.map((key) => ctx(key));
  } catch {
    return [];
  }
}

function loadImagesFromLegacyFolder() {
  try {
    const ctx = require.context('../assets/marquee', false, /\.(png|jpe?g|webp|gif)$/i);
    const keys = ctx.keys();
    return keys.map((key) => ctx(key));
  } catch {
    return [];
  }
}

export default function ImageMarquee() {
  const desktopImages = useMemo(() => {
    const fromDesktop = loadImagesFromDesktopFolder();
    const fallbackRoot = fromDesktop.length ? [] : loadImagesFromLegacyFolder();
    const merged = [...fromDesktop, ...fallbackRoot];
    return merged.length ? merged.slice(0, 6) : [fallbackImage];
  }, []);

  const mobileImages = useMemo(() => {
    const fromMobile = loadImagesFromMobileFolder();
    if (fromMobile.length) {
      return fromMobile.slice(0, 6);
    }
    return desktopImages.slice(0, 6);
  }, [desktopImages]);

  const [isMobileView, setIsMobileView] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const images = isMobileView ? mobileImages : desktopImages;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState('');
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const activeItems = CATEGORY_GROUPS.find((group) => group.title === activeGroup)?.items || [];

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [images.length]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [isMobileView, desktopImages, mobileImages]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const onChange = (event) => setIsMobileView(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onOpen = () => setMobileCategoriesOpen(true);
    window.addEventListener('open-mobile-categories', onOpen);
    return () => window.removeEventListener('open-mobile-categories', onOpen);
  }, []);

  const goNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  const onSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    window.open(`https://www.google.com/search?q=${encodeURIComponent(`${value} electronics components`)}`, '_blank');
  };

  return (
    <section className="market-wrap" aria-label="Project highlights">
      <section className="market-strip" id="categories-anchor" onMouseLeave={() => setActiveGroup('')}>
        <aside className="market-categories">
          <div className="market-categories-head">All Categories</div>
          <div className="market-categories-list">
            {CATEGORY_GROUPS.map((group) => (
              <div key={group.title}>
                <button
                  type="button"
                  className={`market-category-group-title ${activeGroup === group.title ? 'active' : ''}`}
                  onMouseEnter={() => setActiveGroup(group.title)}
                  onClick={() => setActiveGroup((prev) => (prev === group.title ? '' : group.title))}
                  aria-expanded={activeGroup === group.title}
                >
                  <span>{group.title}</span>
                  <span className="market-group-chevron">{'>'}</span>
                </button>
                {activeGroup === group.title && (
                  <div className="market-category-mobile-items">
                    {group.items.map((item) => (
                      <button type="button" className="market-category-item" key={`${group.title}-mobile-${item}`}>
                        <span>{item}</span>
                        <span className="market-category-chevron">{'>'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        <div
          className={`market-category-flyout-overlay ${activeGroup ? 'open' : ''}`}
          onMouseEnter={() => activeGroup && setActiveGroup(activeGroup)}
        >
          {activeGroup && (
            <>
              <div className="market-category-flyout-title">{activeGroup}</div>
              <div className="market-category-group-items open">
                {activeItems.map((item) => (
                  <button type="button" className="market-category-item" key={`${activeGroup}-${item}`}>
                    <span>{item}</span>
                    <span className="market-category-chevron">{'>'}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div
          className={`market-category-mobile-overlay ${mobileCategoriesOpen ? 'open' : ''}`}
          role="dialog"
          aria-modal="false"
          aria-label="Categories"
        >
          <div className="market-category-mobile-overlay-head">
            <span>All Categories</span>
            <button
              type="button"
              className="market-category-mobile-close"
              onClick={() => {
                setMobileCategoriesOpen(false);
                setActiveGroup('');
              }}
            >
              x
            </button>
          </div>
          <div className="market-categories-list">
            {CATEGORY_GROUPS.map((group) => (
              <div key={`mobile-${group.title}`}>
                <button
                  type="button"
                  className={`market-category-group-title ${activeGroup === group.title ? 'active' : ''}`}
                  onClick={() => setActiveGroup((prev) => (prev === group.title ? '' : group.title))}
                  aria-expanded={activeGroup === group.title}
                >
                  <span>{group.title}</span>
                  <span className="market-group-chevron">{'>'}</span>
                </button>
                {activeGroup === group.title && (
                  <div className="market-category-mobile-items">
                    {group.items.map((item) => (
                      <button type="button" className="market-category-item" key={`${group.title}-overlay-${item}`}>
                        <span>{item}</span>
                        <span className="market-category-chevron">{'>'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="market-main">
          <form className="market-search" onSubmit={onSearch}>
            <Search size={20} className={query.trim() ? 'active' : ''} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for products..."
              aria-label="Search components"
            />
          </form>

          <div className="market-banners">
            <div className="market-slider-window">
              <div
                className="market-slider-track"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {images.map((src, index) => (
                  <div className="market-slide" key={`${src}-${index}`}>
                    <img src={src} alt={`Project visual ${index + 1}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>

            {images.length > 1 && (
              <>
                <button className="market-arrow market-arrow-left" onClick={goPrev} aria-label="Previous image">
                  <ChevronLeft size={18} />
                </button>
                <button className="market-arrow market-arrow-right" onClick={goNext} aria-label="Next image">
                  <ChevronRight size={18} />
                </button>
              </>
            )}
          </div>
        </div>

      </section>
    </section>
  );
}
